import { Box } from "@mui/material";
import { useCreateItemMutation } from "lib/features/item/itemService";
import { CreateItemWithDetailDto } from "lib/features/item/types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDuration } from "util/commonHelper";
import ItemFormHeader from "../components/ItemFormHeader";
import DetailsStep from "../steps/DetailStepForm/DetailsStep";
import ItemType from "../steps/ItemTypeForm/ItemType";
import PriceStep from "../steps/PriceStepForm/PriceStep";
import ReviewStep from "../steps/ReviewStepForm/ReviewStep";

interface NewItemFormInterface {
  setIsNewFormOpen: (bool: boolean) => void;
  businessId: number | null;
}

const NewItemForm = ({
  setIsNewFormOpen,
  businessId,
}: NewItemFormInterface) => {
  const [step, setStep] = useState<number>(1);
  const [createItem, { isLoading }] = useCreateItemMutation();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState<CreateItemWithDetailDto>({
    businessId: 0,
    item_type: 1,
    item_name: "",
    headline: "",
    description: "",
    whats_included: "",
    cancellation_policy: "",
    unit_name: "",
    price: 0,
    capacity: 0,
    comission: 0,
    additional_info: "",
    min_age: null,
    max_age: null,
    max_guest: null,
    images: [],
    industry_ids: [1],
    category_ids: [],
    additionalOptions: [],
    duration_applicable: true,
    duration_hours: null,
    duration_minutes: null,
  });

  const handleStepSubmit = async (values: CreateItemWithDetailDto) => {
    setFormValues(values);
    if (step < 4) {
      setStep(step + 1);
    } else {
      try {
        const sanitizeNumber = (value: any) =>
          value === "" || value === undefined || value === null
            ? null
            : Number(value);

        let body = {
          businessId: Number(businessId),
          item_type: Number(values.item_type ?? 1),
          item_name: values.item_name,
          unit_name: values.unit_name,
          headline: values.headline,
          max_guest: sanitizeNumber(values.max_guest),
          min_age: sanitizeNumber(values.min_age),
          max_age: sanitizeNumber(values.max_age),
          price: Number(values.price),
          capacity: Number(values.capacity),
          images: values.images,
          industry_ids: values.industry_ids,
          cancellation_policy: values.cancellation_policy,
          price_description: values.price_description,
          whats_included: values.whats_included,
          selling_points: values.selling_points ?? "",
          offering: values.offering,
          additional_info: values.additional_info,
          category_ids: values.category_ids,
          comission: Number(values.comission),
          description: values.description,
          itemOptions: values.itemOptions,
          additionalOptions: values.additionalOptions,
          duration_applicable: values.duration_applicable ?? true,
          duration: values.duration_applicable
            ? formatDuration(values.duration_hours, values.duration_minutes)
            : undefined,
        };

        await createItem(body).unwrap();
        navigate("/my-items");setIsNewFormOpen(false)
      } catch (error) {}
    }
  };

  const renderCurrentStepForm = () => {
    switch (step) {
      case 1:
        return (
          <ItemType initialValues={formValues} onSubmit={handleStepSubmit} />
        );
      case 2:
        return (
          <DetailsStep
            initialValues={formValues}
            onSubmit={handleStepSubmit}
            setFormValues={setFormValues}
          />
        );
      case 3:
        return (
          <PriceStep
            initialValues={formValues}
            onSubmit={handleStepSubmit}
            setFormValues={setFormValues}
          />
        );
      case 4:
        return (
          <ReviewStep
            initialValues={formValues}
            onSubmit={handleStepSubmit}
            loader={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box width="100%" position="relative">
      {/* Header */}
      <ItemFormHeader
        step={step}
        setStep={setStep}
        setIsEditing={setIsNewFormOpen}
      />

      {/* Form */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "1rem",

          maxWidth: {
            xs: "90vw", // small
            sm: "70vw", // tab
            md: "50vw", // large
          },
          marginRight: "auto",
          marginLeft: "auto",
        }}
      >
        {renderCurrentStepForm()}
      </Box>
    </Box>
  );
};

export default NewItemForm;
